import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Check, Gift, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const TIER_FEATURES = {
  1: {
    name: "Tier 1",
    requests: "500 requests/month",
    features: ["Unlimited staff", "Photo attachments", "QR codes", "All future updates"],
  },
  2: {
    name: "Tier 2", 
    requests: "1,000 requests/month",
    features: ["Everything in Tier 1", "Priority support"],
  },
  3: {
    name: "Tier 3",
    requests: "Unlimited requests",
    features: ["Everything in Tier 2", "First access to new features"],
  },
};

export default function AppSumoPage() {
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [licenseUuid, setLicenseUuid] = useState<string | null>(null);
  const [tier, setTier] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uuid = params.get("license_uuid");
    const tierParam = params.get("tier");
    const errorParam = params.get("error");

    if (uuid) setLicenseUuid(uuid);
    if (tierParam) setTier(parseInt(tierParam) || 1);
    if (errorParam) setError(getErrorMessage(errorParam));
  }, []);

  const getErrorMessage = (code: string) => {
    const messages: Record<string, string> = {
      missing_code: "Missing authorization code from AppSumo",
      token_exchange_failed: "Failed to verify with AppSumo. Please try again.",
      license_fetch_failed: "Failed to fetch your license. Please try again.",
      oauth_failed: "Authentication failed. Please try again.",
    };
    return messages[code] || "An error occurred. Please try again.";
  };

  const handleLinkLicense = async () => {
    if (!licenseUuid) return;
    
    setIsLinking(true);
    setError(null);
    
    try {
      const res = await api.post("/api/appsumo/link", { licenseUuid });
      if (res.ok) {
        setLocation("/dashboard?appsumo=activated");
      } else {
        const data = await res.json();
        setError(data.error?.message || "Failed to activate license");
      }
    } catch {
      setError("Failed to activate license. Please try again.");
    } finally {
      setIsLinking(false);
    }
  };

  const tierInfo = TIER_FEATURES[tier as keyof typeof TIER_FEATURES] || TIER_FEATURES[1];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Gift className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Welcome, Sumo-ling!</CardTitle>
          <CardDescription>
            {licenseUuid 
              ? "Your AppSumo license is ready to activate"
              : "Redeem your AppSumo deal"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {licenseUuid && (
            <div className="bg-muted rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Your Deal</span>
                <Badge variant="secondary">{tierInfo.name}</Badge>
              </div>
              <div className="text-2xl font-bold text-primary">{tierInfo.requests}</div>
              <ul className="space-y-2">
                {tierInfo.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {authLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : user ? (
            // User is logged in - link license
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Signed in as {user.email}
              </p>
              {licenseUuid ? (
                <Button 
                  className="w-full" 
                  size="lg" 
                  onClick={handleLinkLicense}
                  disabled={isLinking}
                >
                  {isLinking ? "Activating..." : "Activate License"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <p className="text-center text-muted-foreground">
                  No license found. Please click "Activate" from your AppSumo purchase.
                </p>
              )}
            </div>
          ) : (
            // User not logged in - show signup/login options
            <div className="space-y-4">
              <Link href={`/signup${licenseUuid ? `?appsumo=${licenseUuid}&tier=${tier}` : ""}`}>
                <Button className="w-full" size="lg">
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <div className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link 
                  href={`/login${licenseUuid ? `?appsumo=${licenseUuid}&tier=${tier}` : ""}`}
                  className="text-primary hover:underline"
                >
                  Sign in
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}







